import {
  Briefcase,
  Calendar,
  Clock,
  FileText,
  PhoneForwarded,
  Sparkles,
} from 'lucide-react';

import { IndustryPage, type IndustryPageConfig } from '@/components/marketing/industry-page';

const config: IndustryPageConfig = {
  seoTitle: 'AI Receptionist for Professional Services — Client Intake & Call Answering 24/7',
  seoDescription:
    'An AI receptionist for accountants, consultants, financial advisers, and professional service firms. Captures client inquiries, answers FAQs, and transfers priority calls — 24/7.',
  path: '/industries/professional-services',
  badge: 'Professional Services',
  headline: 'First impressions that sound like your best employee — 24/7.',
  subheadline:
    'An AI receptionist that captures client inquiries, answers service and fee questions, and routes priority calls to the right person — so your firm always sounds professional, even out of hours.',
  pains: [
    'Prospects call after hours, reach a generic voicemail, and choose a competitor who sounds more available.',
    'Fee and service questions consume time from billable work — especially for sole practitioners.',
    'Inconsistent phone manner when admin staff are unavailable undermines the firm\'s professional image.',
  ],
  features: [
    {
      icon: Briefcase,
      title: 'Service & fee Q&A',
      desc: 'Your services, fee structures, engagement process, and turnaround times — answered from your approved content, every time.',
    },
    {
      icon: Calendar,
      title: 'Consultation request capture',
      desc: 'Captures prospect name, contact, service interest, and preferred meeting time. Ready for your team to follow up.',
    },
    {
      icon: PhoneForwarded,
      title: 'Priority call routing',
      desc: 'Existing clients with urgent matters get transferred to the right person immediately — with context.',
    },
    {
      icon: Sparkles,
      title: 'Consistent professional tone',
      desc: 'Configure the greeting, name, and persona to match your firm brand. Every caller receives the same polished experience.',
    },
    {
      icon: Clock,
      title: '24/7 out-of-hours coverage',
      desc: 'Client inquiries answered at 7pm and on weekends. No leads lost to out-of-hours voicemail.',
    },
    {
      icon: FileText,
      title: 'Inquiry log & summaries',
      desc: 'All calls logged with AI summaries. Review and prioritise follow-up from one dashboard.',
    },
  ],
  faqs: [
    {
      q: 'Can it handle calls for an accounting firm?',
      a: 'Yes — configure your tax services, BAS lodgement process, pricing tiers, and deadline schedule. The agent answers from that content.',
    },
    {
      q: 'What about financial advisers with compliance requirements?',
      a: 'The agent answers from your approved content only and does not provide financial advice. Configure it to capture inquiries and route to your licensed adviser for substantive discussions.',
    },
    {
      q: 'Can it handle multiple consultants with different specialisations?',
      a: 'Yes — include per-consultant specialisation info in your knowledge base and configure routing rules accordingly.',
    },
    {
      q: 'How does it handle confidential client calls?',
      a: 'Existing clients with confidential matters can be transferred directly to their adviser. The agent captures minimal information and escalates immediately when configured to do so.',
    },
  ],
  ctaHeadline: 'Never lose a client to voicemail again.',
  ctaSub: 'Professional AI receptionist for service firms. Live in under 10 minutes.',
};

export function ProfessionalServicesIndustryPage(): JSX.Element {
  return <IndustryPage config={config} />;
}
